import {
  Text,
  Switch,
  Dropdown,
  Option,
  Badge,
  Button,
} from '@fluentui/react-components';
import {
  PaintBrushRegular,
  PersonRegular,
  InfoRegular,
  WeatherMoonRegular,
  WeatherSunnyRegular,
  GlobeRegular,
  SignOutRegular,
} from '@fluentui/react-icons';
import { useIntl } from 'react-intl';

interface SettingsPageProps {
  darkMode: boolean;
  onToggleDarkMode: (next: boolean) => void;
  userName: string;
  userEmail?: string;
  onSignOut: () => void;
  appVersion?: string;
}

const APP_VERSION_FALLBACK = 'dev';
const SUPPORTED_LANGUAGE_IDS = [
  { id: 'en-GB', labelKey: 'settings.language.enGB' },
  { id: 'ar', labelKey: 'settings.language.ar', disabled: true },
];

export function SettingsPage({
  darkMode,
  onToggleDarkMode,
  userName,
  userEmail,
  onSignOut,
  appVersion = APP_VERSION_FALLBACK,
}: SettingsPageProps) {
  const intl = useIntl();
  const currentLanguageId = SUPPORTED_LANGUAGE_IDS[0];

  return (
    <div className="settings-page">
      <div>
        <Text size={500} weight="semibold">{intl.formatMessage({ id: 'settings.title' })}</Text>
        <Text size={200} className="text-muted" block>
          {intl.formatMessage({ id: 'settings.subtitle' })}
        </Text>
      </div>

      {/* Appearance */}
      <section className="settings-section">
        <Text className="settings-section-title" as="h3">
          <PaintBrushRegular className="icon-section" />
          {intl.formatMessage({ id: 'settings.section.appearance' })}
        </Text>

        <div className="settings-row">
          <div className="settings-row-label">
            <Text weight="semibold">{intl.formatMessage({ id: 'settings.theme.label' })}</Text>
            <Text size={200} className="text-muted">
              {intl.formatMessage({ id: 'settings.theme.description' })}
            </Text>
          </div>
          <div className="flex-row gap-sm">
            {darkMode ? <WeatherMoonRegular /> : <WeatherSunnyRegular />}
            <Switch
              checked={darkMode}
              onChange={(_e, data) => onToggleDarkMode(data.checked)}
              label={darkMode ? intl.formatMessage({ id: 'settings.theme.dark' }) : intl.formatMessage({ id: 'settings.theme.light' })}
              labelPosition="after"
            />
          </div>
        </div>

        <div className="settings-row">
          <div className="settings-row-label">
            <Text weight="semibold">{intl.formatMessage({ id: 'settings.language.label' })}</Text>
            <Text size={200} className="text-muted">
              {intl.formatMessage({ id: 'settings.language.description' })}
            </Text>
          </div>
          <Dropdown
            className="page-filters-dropdown"
            value={intl.formatMessage({ id: currentLanguageId.labelKey })}
            selectedOptions={[currentLanguageId.id]}
            disabled
          >
            {SUPPORTED_LANGUAGE_IDS.map(lang => (
              <Option key={lang.id} value={lang.id} disabled={lang.disabled}>
                {intl.formatMessage({ id: lang.labelKey })}
              </Option>
            ))}
          </Dropdown>
        </div>
      </section>

      {/* Account */}
      <section className="settings-section">
        <Text className="settings-section-title" as="h3">
          <PersonRegular className="icon-section" />
          {intl.formatMessage({ id: 'settings.section.account' })}
        </Text>

        <div className="settings-row">
          <div className="settings-row-label">
            <Text weight="semibold">{userName}</Text>
            {userEmail && (
              <Text size={200} className="text-muted">{userEmail}</Text>
            )}
          </div>
          <Button
            icon={<SignOutRegular />}
            appearance="secondary"
            onClick={onSignOut}
          >
            {intl.formatMessage({ id: 'settings.signOut' })}
          </Button>
        </div>
      </section>

      {/* About */}
      <section className="settings-section">
        <Text className="settings-section-title" as="h3">
          <InfoRegular className="icon-section" />
          {intl.formatMessage({ id: 'settings.section.about' })}
        </Text>

        <div className="settings-row">
          <div className="settings-row-label">
            <Text weight="semibold">{intl.formatMessage({ id: 'settings.appName' })}</Text>
            <Text size={200} className="text-muted">
              {intl.formatMessage({ id: 'settings.appDescription' })}
            </Text>
          </div>
          <Badge appearance="outline" size="small">v{appVersion}</Badge>
        </div>

        <div className="settings-row">
          <div className="settings-row-label">
            <Text weight="semibold">{intl.formatMessage({ id: 'settings.region.label' })}</Text>
            <Text size={200} className="text-muted">
              {intl.formatMessage({ id: 'settings.region.description' })}
            </Text>
          </div>
          <Badge appearance="outline" size="small" icon={<GlobeRegular />}>{intl.formatMessage({ id: 'settings.region.badge' })}</Badge>
        </div>
      </section>
    </div>
  );
}
